import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as adminCommunity from '../../services/adminCommunity.service';
import { createPost } from '../../services/community.service';

export default function AdminCommunity() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [composing, setComposing] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setPosts(await adminCommunity.listAdminPosts());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load posts.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (post) => {
    if (expandedId === post.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(post.id);
    setDetail(null);
    const data = await adminCommunity.getAdminPostDetail(post.id);
    setDetail(data);
  };

  const hidePost = async (post) => {
    const reason = window.prompt(`Reason for hiding this post? (shown to staff only)`, '');
    if (reason === null) return; // cancelled
    await adminCommunity.setPostHidden(post.id, true, reason || null);
    await load();
    if (expandedId === post.id) toggleReload(post.id);
  };

  const unhidePost = async (post) => {
    await adminCommunity.setPostHidden(post.id, false, null);
    await load();
    if (expandedId === post.id) toggleReload(post.id);
  };

  const deletePostPermanently = async (post) => {
    if (!window.confirm('Delete this post permanently? This cannot be undone — use "Hide" instead if you might want it back.')) return;
    await adminCommunity.deletePost(post.id);
    if (expandedId === post.id) { setExpandedId(null); setDetail(null); }
    await load();
  };

  const deleteCommentPermanently = async (comment) => {
    if (!window.confirm('Delete this comment permanently? This cannot be undone.')) return;
    await adminCommunity.deleteComment(comment.id);
    toggleReload(expandedId);
    await load();
  };

  const hideComment = async (comment) => {
    const reason = window.prompt(`Reason for hiding this comment? (shown to staff only)`, '');
    if (reason === null) return;
    await adminCommunity.setCommentHidden(comment.id, true, reason || null);
    toggleReload(expandedId);
  };

  const unhideComment = async (comment) => {
    await adminCommunity.setCommentHidden(comment.id, false, null);
    toggleReload(expandedId);
  };

  const toggleReload = async (postId) => {
    const data = await adminCommunity.getAdminPostDetail(postId);
    setDetail(data);
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <AdminNav />

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold text-reset-brown">Community Moderation</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {posts ? `${posts.length} post${posts.length === 1 ? '' : 's'}` : '—'}
            </p>
          </div>
          {!composing && (
            <button
              onClick={() => setComposing(true)}
              className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors"
            >
              + Compose weekly piece
            </button>
          )}
        </div>

        {composing && (
          <WeeklyPieceComposer
            onCancel={() => setComposing(false)}
            onPosted={() => { setComposing(false); load(); }}
          />
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {!posts ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            No posts yet.
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <div key={post.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-semibold text-reset-brown">
                          {post.author?.name || '(deleted member)'}
                        </span>
                        {post.is_staff_seeded && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-terracotta/10 text-reset-terracotta">
                            Staff prompt
                          </span>
                        )}
                        {post.is_hidden && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            Hidden{post.hidden_reason ? `: ${post.hidden_reason}` : ''}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-line">{post.body}</p>
                      <p className="text-xs text-gray-400 mt-1.5">
                        {new Date(post.created_at).toLocaleString(undefined, { hour12: true })} · {post.comment_count} comment{post.comment_count === '1' ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <button
                        onClick={() => (post.is_hidden ? unhidePost(post) : hidePost(post))}
                        className="text-xs font-medium text-red-400 hover:text-red-600 whitespace-nowrap"
                      >
                        {post.is_hidden ? 'Unhide post' : 'Hide post'}
                      </button>
                      <button
                        onClick={() => deletePostPermanently(post)}
                        className="text-xs font-medium text-red-700 hover:text-red-900 whitespace-nowrap"
                      >
                        Delete permanently
                      </button>
                      <button
                        onClick={() => toggleExpand(post)}
                        className="text-xs font-medium text-reset-terracotta hover:underline whitespace-nowrap"
                      >
                        {expandedId === post.id ? 'Hide comments' : 'View comments'}
                      </button>
                    </div>
                  </div>
                </div>

                {expandedId === post.id && (
                  <div className="border-t border-gray-50 bg-gray-50/50 px-5 py-3">
                    {!detail ? (
                      <p className="text-xs text-gray-400">Loading comments…</p>
                    ) : detail.comments.length === 0 ? (
                      <p className="text-xs text-gray-400">No comments on this post.</p>
                    ) : (
                      <div className="space-y-2.5">
                        {detail.comments.map(comment => (
                          <div key={comment.id} className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-reset-brown">
                                  {comment.author?.name || '(deleted member)'}
                                </span>
                                {comment.is_hidden && (
                                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                                    Hidden{comment.hidden_reason ? `: ${comment.hidden_reason}` : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5">{comment.body}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <button
                                onClick={() => (comment.is_hidden ? unhideComment(comment) : hideComment(comment))}
                                className="text-[11px] font-medium text-red-400 hover:text-red-600 whitespace-nowrap"
                              >
                                {comment.is_hidden ? 'Unhide' : 'Hide'}
                              </button>
                              <button
                                onClick={() => deleteCommentPermanently(comment)}
                                className="text-[11px] font-medium text-red-700 hover:text-red-900 whitespace-nowrap"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Weekly piece composer ───────────────────────────────────────────────────
// The team's one longer piece a week — Cup-of-Jo-style card in the feed
// (image/title/blurb), full page with comments below. Same fields the
// backend has already accepted since Phase 3's original build; this was
// the "manual API today, admin screen as a fast follow-up" gap closing.

function WeeklyPieceComposer({ onCancel, onPosted }) {
  const [title, setTitle] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createPost({
        title: title.trim(),
        cover_image_url: coverImageUrl.trim() || null,
        excerpt: excerpt.trim() || null,
        body: body.trim(),
        is_staff_seeded: true,
      });
      onPosted();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post this.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 space-y-3 mb-6">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">{error}</div>}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input
          value={title} onChange={e => setTitle(e.target.value)} required
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Cover image URL (optional — Google Drive share links work)</label>
        <input
          value={coverImageUrl} onChange={e => setCoverImageUrl(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Blurb (shown in the feed card)</label>
        <textarea
          value={excerpt} onChange={e => setExcerpt(e.target.value)} rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Full piece</label>
        <textarea
          value={body} onChange={e => setBody(e.target.value)} required rows={8}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
          Cancel
        </button>
        <button
          type="submit" disabled={saving}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Posting…' : 'Post to the Hive'}
        </button>
      </div>
    </form>
  );
}
